/**
 * Web Modal Component
 *
 */

import React from 'react'
import clsx from 'clsx'
import { SuffixContext } from '../../shared/helpers/Suffix'
import type { ContextProps } from '../../shared/Context'
import Context from '../../shared/Context'
import {
  warn,
  makeUniqueId,
  extendPropsWithContextInClassComponent,
  processChildren,
  dispatchCustomElementEvent,
} from '../../shared/component-helper'
import { createSpacingClasses } from '../space/SpacingHelper'
import HelpButtonInstance from '../help-button/HelpButtonInstance'
import { getListOfModalRoots, getModalRoot } from './helpers'
import ModalInner from './parts/ModalInner'
import type { ModalProps } from './types'

import ModalHeader from './parts/ModalHeader'
import ModalHeaderBar from './parts/ModalHeaderBar'
import type { ScrollViewAllProps } from '../../fragments/scroll-view/ScrollView'
import CloseButton from './parts/CloseButton'
import ModalRoot from './ModalRoot'
import { ParagraphContext } from '../../elements/typography/P'
import type { SpacingProps } from '../../shared/types'
import type { ButtonProps } from '../button/Button'

export const ANIMATION_DURATION = 300

type ModalState = {
  hide: boolean
  modalActive: boolean
  preventAutoFocus: boolean
}

export type ModalAllProps = ModalProps &
  SpacingProps &
  Omit<ScrollViewAllProps, 'children'>

class Modal extends React.PureComponent<ModalAllProps, ModalState> {
  static contextType = Context

  context!: ContextProps

  static Bar = ModalHeaderBar
  static Header = ModalHeader
  static Content = ModalInner

  static getContent(props) {
    if (typeof props.modalContent === 'string') {
      return props.modalContent
    } else if (typeof props.modalContent === 'function') {
      return props.modalContent(props)
    }
    return processChildren(props)
  }

  _id: string
  _triggerRef: React.RefObject<HTMLElement>
  _onUnmount: Array<() => void>
  _openTimeout: NodeJS.Timeout
  _closeTimeout: NodeJS.Timeout
  _sideEffectsTimeout: NodeJS.Timeout
  _tryToOpenTimeout: NodeJS.Timeout
  activeElement: Element
  isInTransition: boolean
  modalContentCloseRef: React.RefObject<
    (event: Event, options: { triggeredBy?: string }) => void
  >

  state = {
    hide: false,
    modalActive: false,
    preventAutoFocus: true,
    animationDuration: ANIMATION_DURATION,
    noAnimation: false,
  }

  static defaultProps = {
    id: null,
    focusSelector: null,
    labelledBy: null,
    title: null,
    disabled: null,
    spacing: true,
    openDelay: null,
    contentId: null,
    dialogTitle: 'Vindu',
    closeTitle: 'Lukk', // Close Modal Window
    hideCloseButton: false,
    closeButtonAttributes: null,
    preventClose: false,
    preventCoreStyle: false,
    animationDuration: ANIMATION_DURATION,
    noAnimation: false,
    noAnimationOnMobile: false,
    fullscreen: 'auto',
    minWidth: null,
    maxWidth: null,
    alignContent: 'left',
    containerPlacement: null,
    verticalAlignment: null,
    open: null,
    directDomReturn: false,
    omitTriggerButton: false,

    className: null,
    children: null,

    onOpen: null,
    onClose: null,
    onClosePrevent: null,
    openModal: null,
    closeModal: null,

    trigger: null,
    triggerAttributes: null,

    overlayClass: null,
    contentClass: null,

    modalContent: null,
    headerContent: null,
    barContent: null,
  }

  static getDerivedStateFromProps(props, state) {
    if (typeof window !== 'undefined' && window['IS_TEST']) {
      state.animationDuration = 0
      state.noAnimation = true
    } else {
      state.animationDuration = props.animationDuration
      state.noAnimation = props.noAnimation
    }

    if (props.open !== state._open) {
      if (props.open === true) {
        state.hide = false
        if (state.noAnimation) {
          state.modalActive = true
        }
      } else if (props.open === false) {
        state.hide = true
        if (state.noAnimation) {
          state.modalActive = false
        }
      }
    }
    state._open = props.open

    return state
  }

  constructor(props) {
    super(props)
    this._id = props.id || makeUniqueId('modal-')

    this._triggerRef = React.createRef()
    this.modalContentCloseRef = React.createRef()

    this._onUnmount = []
  }

  componentDidMount() {
    this.openBasedOnStateUpdate()
  }

  componentWillUnmount() {
    clearTimeout(this._openTimeout)
    clearTimeout(this._closeTimeout)

    this.removeActiveState()

    this._onUnmount.forEach((fn) => {
      if (typeof fn === 'function') {
        fn()
      }
    })
  }

  componentDidUpdate(prevProps) {
    // Don't interfere if modal is currently transitioning, added to fix an issue with rapid state changes in React v19.
    // Could be considered to be removed in the future, when Eufemia is using React v19.
    if (this.isInTransition) {
      return
    }
    if (prevProps !== this.props) {
      this.openBasedOnStateUpdate()
    }
  }

  openBasedOnStateUpdate() {
    const { hide } = this.state
    const { open } = this.props

    if (!this.activeElement && typeof document !== 'undefined') {
      this.activeElement = document.activeElement
    }

    if (!hide && open === true) {
      this.toggleOpenClose(null, true)
    } else if (hide && open === false) {
      this.toggleOpenClose(null, false)
    }
  }

  toggleOpenClose = (event = null, showModal = null) => {
    if (event && event.preventDefault) {
      event.preventDefault()
    }

    const toggleNow = () => {
      const {
        animationDuration = ANIMATION_DURATION,
        noAnimation = false,
      } = this.state
      const timeoutDuration =
        typeof animationDuration === 'string'
          ? parseFloat(animationDuration)
          : animationDuration

      const modalActive =
        typeof showModal === 'boolean'
          ? showModal
          : !this.state.modalActive

      this.isInTransition = true

      const doItNow = () => {
        this.setState(
          {
            hide: false,
            modalActive,
          },
          () => {
            this.isInTransition = false
            this.handleSideEffects()
          }
        )
      }

      if (modalActive === false && !noAnimation) {
        this.setState({
          hide: true,
        })

        this._closeTimeout = setTimeout(doItNow, timeoutDuration) // delay because of the animation
      } else {
        doItNow()
      }
    }

    const waitBeforeOpen = () => {
      const { openDelay } = this.props
      const { noAnimation } = this.state
      const delay =
        typeof openDelay === 'string' ? parseFloat(openDelay) : openDelay
      if (delay > 0 && !noAnimation) {
        this._openTimeout = setTimeout(toggleNow, delay) // custom delay
      } else {
        toggleNow()
      }
    }

    clearTimeout(this._closeTimeout)
    clearTimeout(this._openTimeout)

    const { openModal } = this.props
    if (typeof openModal === 'function') {
      const fn = openModal(waitBeforeOpen, this)
      if (fn) {
        this._onUnmount.push(fn)
      }
    } else {
      waitBeforeOpen()
    }
  }

  handleSideEffects = () => {
    const { modalActive, preventAutoFocus, animationDuration } = this.state
    const { closeModal, open } = this.props

    if (modalActive) {
      if (typeof closeModal === 'function') {
        const fn = closeModal(() => {
          this.toggleOpenClose(null, false)
        }, this)
        if (fn) {
          this._onUnmount.push(fn)
        }
      }
      this.setActiveState(this._id)
    } else if (modalActive === false && !preventAutoFocus) {
      const focus = (elem: HTMLElement) => {
        // So we can omit showing a Tooltip on the trigger button
        elem.setAttribute('data-autofocus', 'true')
        elem.focus({ preventScroll: true })

        return new Promise<void>((resolve) => {
          setTimeout(
            () => {
              elem?.removeAttribute('data-autofocus')
              resolve()
            },
            parseFloat(String(animationDuration)) / 3
          )
        })
      }

      if (this._triggerRef?.current) {
        focus(this._triggerRef.current as HTMLElement)
      }

      // because the open was set to true, we force
      if (open === true && this.activeElement instanceof HTMLElement) {
        try {
          focus(this.activeElement).then(() => {
            this.activeElement = null
          })
        } catch (e) {
          //
        }
      }

      this.removeActiveState()
    }

    if (preventAutoFocus) {
      this.setState({ preventAutoFocus: false })
    }
  }

  open = (e: Event) => {
    this.toggleOpenClose(e, true)
  }

  close = (
    event?: Event | React.SyntheticEvent,
    {
      ifIsLatest,
      triggeredBy = 'handler',
    }: { ifIsLatest?: boolean; triggeredBy?: string } = {
      ifIsLatest: true,
    }
  ) => {
    this.modalContentCloseRef.current?.(event as Event, { triggeredBy })

    const { preventClose = false } = this.props

    if (preventClose) {
      const id = this._id
      dispatchCustomElementEvent(this, 'onClosePrevent', {
        id,
        event,
        triggeredBy,
        close: (e) => {
          this.toggleOpenClose(e, false)
        },
      })
    } else {
      if (ifIsLatest) {
        const list = getListOfModalRoots()
        if (list.length > 1) {
          const last = getModalRoot(-1)
          if (last !== this) {
            return // stop here
          }
        }
      }

      this.toggleOpenClose(event, false)
    }
  }

  removeActiveState() {
    const last = getModalRoot(-1)

    // If this instance is not the last one,
    // make the current one to as the active one
    if (last?._id && last._id !== this._id) {
      return this.setActiveState(last._id)
    }

    try {
      document.documentElement.removeAttribute('data-dnb-modal-active')
    } catch (e) {
      warn('Modal: Error on remove "data-dnb-modal-active"', e)
    }
  }

  /**
   * Prevent scrolling on the background
   * But checks if this instance was the last one or not
   *
   * @param {string} modalId Will remove the attribute if false is given
   */
  setActiveState(modalId: string) {
    if (!modalId) {
      warn('Modal: A valid modalId is required')
    }
    // prevent scrolling on the background
    if (typeof document !== 'undefined') {
      try {
        document.documentElement.setAttribute(
          'data-dnb-modal-active',
          modalId
        )
      } catch (e) {
        warn('Modal: Error on set "data-dnb-modal-active"', e)
      }
    }
  }

  render() {
    const visualTestsPropsOverride =
      typeof window !== 'undefined' && window['IS_TEST']
        ? {
            animationDuration: 0,
            noAnimation: true,
          }
        : {}

    // use only the props from context, who are available here anyway
    const props = extendPropsWithContextInClassComponent(
      this.props,
      Modal.defaultProps,
      this.context.getTranslation(this.props).Modal,
      this.context.Modal,
      visualTestsPropsOverride
    )

    const {
      contentId = null,
      disabled = null,
      labelledBy = null,
      focusSelector = null,
      headerContent = null,
      barContent = null,
      bypassInvalidationSelectors = null,
      verticalAlignment = 'center',

      id,
      openDelay,

      omitTriggerButton = false,
      trigger = null,
      triggerAttributes = null,
      ref: _ref,
      ...rest
    } = props

    const { hide, modalActive } = this.state
    const modalContent = Modal.getContent(
      typeof this.props.children === 'function'
        ? Object.freeze({
            ...this.props,
            close: this.close,
          })
        : this.props
    )

    const render = (suffixProps) => {
      const usedTriggerAttributes = {
        hidden: false,
        variant: 'secondary',
        iconPosition: 'left',
        ...triggerAttributes,
      } as ButtonProps

      if (disabled) {
        usedTriggerAttributes.disabled = true
      }

      if (usedTriggerAttributes.id) {
        this._id = usedTriggerAttributes.id
      }

      let fallbackTitle: string
      if (usedTriggerAttributes.title) {
        fallbackTitle = usedTriggerAttributes.title
      }
      // in case the modal is used in suffix and no title is given
      // suffixProps.label is also available, so we could use that too
      else if (suffixProps) {
        fallbackTitle = this.context.translation.HelpButton.title
      }

      const headerTitle = rest.title || fallbackTitle
      const title = (
        !usedTriggerAttributes?.text && headerTitle
          ? headerTitle || fallbackTitle
          : null
      ) as string

      const TriggerButton = trigger
        ? (trigger as () => React.JSX.Element)
        : HelpButtonInstance

      return (
        <>
          {TriggerButton && !omitTriggerButton && (
            <TriggerButton
              {...usedTriggerAttributes}
              id={this._id}
              title={title}
              onClick={this.toggleOpenClose}
              ref={this._triggerRef}
              className={clsx(
                'dnb-modal__trigger',
                createSpacingClasses(rest as SpacingProps),
                usedTriggerAttributes.className
              )}
            />
          )}

          {modalActive && modalContent && (
            <ParagraphContext value={{ isNested: false }}>
              <ModalRoot
                {...rest}
                id={this._id}
                contentId={contentId || `dnb-modal-${this._id}`}
                labelledBy={labelledBy}
                focusSelector={focusSelector}
                modalContent={modalContent}
                headerContent={headerContent}
                verticalAlignment={verticalAlignment}
                barContent={barContent}
                bypassInvalidationSelectors={bypassInvalidationSelectors}
                close={this.close}
                hide={hide}
                title={headerTitle}
                modalContentCloseRef={this.modalContentCloseRef}
              />
            </ParagraphContext>
          )}
        </>
      )
    }

    return <SuffixContext.Consumer>{render}</SuffixContext.Consumer>
  }
}

export { CloseButton, Modal as OriginalComponent }

export default Modal
